<template>
  <div v-if="selectedAsset" class="asset-settings">
    <!-- Breadcrumb -->
    <nav class="crumbs" aria-label="Breadcrumb">
      <button type="button" class="crumb" @click="goAssets">Assets</button>
      <v-icon size="14" color="#5c6f78">mdi-chevron-right</v-icon>
      <button type="button" class="crumb" @click="goAsset">{{ selectedAsset.name }}</button>
      <v-icon size="14" color="#5c6f78">mdi-chevron-right</v-icon>
      <span class="crumb current">Settings</span>
    </nav>

    <!-- Heading -->
    <header class="settings-head">
      <h1 class="settings-title">Asset settings</h1>
      <p class="settings-sub">
        Details, notifications, cover image, model versions and scans for {{ selectedAsset.name }}.
      </p>
    </header>

    <!-- Tabs -->
    <div class="tabbar" role="tablist">
      <button type="button" class="tab" :class="{ active: tab === 'general' }" role="tab"
        @click="tab = 'general'">General</button>

      <v-tooltip :disabled="!(isLoaded && noScansAvailable)" location="top" :text="disabledTooltip">
        <template #activator="{ props }">
          <button v-bind="props" type="button" class="tab"
            :class="{ active: tab === 'versions', disabled: isLoaded && noScansAvailable }" role="tab"
            :disabled="isLoaded && noScansAvailable" @click="!(isLoaded && noScansAvailable) && (tab = 'versions')">
            Model versions
            <span v-if="isLoaded" class="tab-count">[{{ versionCount }}]</span>
            <span v-else class="tab-count-sk" aria-hidden="true"></span>
          </button>
        </template>
      </v-tooltip>

      <button type="button" class="tab" :class="{ active: tab === 'scans' }" role="tab" @click="tab = 'scans'">
        Scans
        <span v-if="isLoaded" class="tab-count">[{{ scanCount }}]</span>
        <span v-else class="tab-count-sk" aria-hidden="true"></span>
        <v-icon v-if="isLoaded && noScansAvailable" size="14" color="#f59e0b" class="tab-alert">mdi-alert-circle-outline</v-icon>
      </button>
    </div>

    <!-- Tab content -->
    <div class="tab-body">
      <UpdateAssetAdminCard v-if="tab === 'general'" />
      <AssetAdminViewModelVersions v-else-if="tab === 'versions'" :assetId="selectedAsset.id" @changed="loadCounts" />
      <AssetAdminViewScans v-else-if="tab === 'scans'" @OnScanCreated="onScanCreated" />
    </div>
  </div>

  <div v-else class="settings-empty">
    <v-icon size="48" color="#4a5a60">mdi-cog-outline</v-icon>
    <p>No asset selected. Pick an asset from the list first.</p>
    <button type="button" class="btn-back" @click="goAssets">Back to assets</button>
  </div>
</template>

<script>
import UpdateAssetAdminCard from '@/components/cards/UpdateAssetAdminCard.vue';
import AssetAdminViewModelVersions from './AssetAdminViewModelVersions.vue';
import AssetAdminViewScans from './AssetAdminViewScans.vue';
import { useAssetStore } from '@/store/assetStore';
import ModelVersionService from '@/services/ModelVersionService';

export default {
  name: 'AssetAdmin',
  components: {
    UpdateAssetAdminCard,
    AssetAdminViewModelVersions,
    AssetAdminViewScans,
  },
  data() {
    return {
      tab: 'general',
      selectedAsset: null,
      assetStore: null,
      scanCount: 0,
      versionCount: 0,
      noScansAvailable: true,
      isLoaded: false,
      disabledTooltip: 'Upload a scan first — model versions are built from processed scans.',
    };
  },
  async beforeMount() {
    this.assetStore = useAssetStore();
    this.selectedAsset = this.assetStore.asset;
    if (this.selectedAsset) await this.loadCounts();
  },
  methods: {
    async loadCounts() {
      try {
        const [scans, versions] = await Promise.all([
          ModelVersionService.getScansByAssetId(this.selectedAsset.id).catch(() => []),
          ModelVersionService.getModelVersionsByAssetId(this.selectedAsset.id).catch(() => []),
        ]);
        this.scanCount = (scans || []).length;
        this.versionCount = (versions || []).length;
        this.noScansAvailable = this.scanCount === 0;
      } finally {
        this.isLoaded = true;
      }
    },
    async onScanCreated() {
      this.noScansAvailable = false;
      await this.loadCounts();
    },
    goAssets() {
      this.$router.push({ name: 'Assets' });
    },
    goAsset() {
      this.$router.push({ name: 'AssetLanding', params: { id: this.selectedAsset.id } });
    },
  },
};
</script>

<style scoped>
.asset-settings {
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

/* Breadcrumb */
.crumbs { display: flex; align-items: center; gap: 6px; font-size: 0.82rem; margin-bottom: 10px; }
.crumb {
  background: none; border: none; padding: 0; font: inherit; font-size: 0.82rem;
  color: #8aa0aa; cursor: pointer;
}
.crumb:hover:not(.current) { color: #cfd8dc; }
.crumb.current { color: #fff; font-weight: 600; cursor: default; }

/* Heading */
.settings-head { margin-bottom: 18px; }
.settings-title { font-size: clamp(1.5rem, calc(12px + 1vw), 1.9rem); font-weight: 800; color: #fff; margin: 0; }
.settings-sub { margin: 6px 0 0; font-size: 0.92rem; color: #8aa0aa; }

/* Tabs */
.tabbar {
  display: flex; gap: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 22px;
  overflow-x: auto;
  scrollbar-width: none;
}
.tabbar::-webkit-scrollbar { display: none; }
.tab {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 4px; margin: 0 12px -1px 0;
  background: none; border: none; border-bottom: 2px solid transparent;
  color: #8aa0aa; font: inherit; font-size: 0.92rem; font-weight: 600; cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease, border-color 0.15s ease;
}
.tab:hover:not(.disabled):not(.active) { color: #cfd8dc; }
.tab.active { color: #fff; border-bottom-color: var(--duallo-accent); }
.tab.disabled { color: #4a5a60; cursor: not-allowed; }
.tab-count { color: #667a83; font-weight: 600; }
.tab.active .tab-count { color: #8aa0aa; }
.tab-alert { flex: 0 0 auto; }

/* Shimmer chip shown in place of the count while the scan/version totals are still loading — avoids
   flashing the "no scans" alert / disabled state before we actually know. */
.tab-count-sk {
  display: inline-block; width: 20px; height: 0.78rem; border-radius: 5px;
  background: rgba(255, 255, 255, 0.08); position: relative; overflow: hidden; vertical-align: middle;
}
.tab-count-sk::after {
  content: ""; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.14), transparent);
  animation: tab-count-sk 1.3s ease-in-out infinite;
}
@keyframes tab-count-sk { 100% { transform: translateX(100%); } }
@media (prefers-reduced-motion: reduce) { .tab-count-sk::after { animation: none; } }

.tab-body { flex: 1 1 auto; min-width: 0; min-height: 0; }

/* Empty state */
.settings-empty {
  min-height: 50vh;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 12px; color: #8aa0aa; text-align: center;
}
.btn-back {
  padding: 9px 18px; border-radius: 10px;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.14);
  color: #e6edf0; font: inherit; font-weight: 600; cursor: pointer;
}
.btn-back:hover { background: rgba(255, 255, 255, 0.08); }
</style>
